import React, { useState } from 'react';
import { Cpu, Activity, Code, Server, ArrowRight, Layers, CircuitBoard, Microchip, Settings, Binary } from 'lucide-react';

/**
 * Enhanced Capabilities/Services Component - Inspired by Smartsocs Turnkey Services
 * - Card-based layout with hover effects
 * - Interactive service selection
 * - Visual icons and descriptions
 */

const services = [
    {
        id: "design-verification",
        icon: Binary,
        title: "Design Verification",
        shortDesc: "UVM, Formal, Emulation",
        color: "#55C3BD",
        fullDesc: "Comprehensive verification solutions using industry-standard methodologies",
        capabilities: [
            "UVM-based constrained random verification",
            "Formal verification & property checking",
            "Emulation & FPGA prototyping",
            "SoC-level integration verification",
            "Coverage-driven verification closure",
            "Low-power verification (UPF/CPF)"
        ]
    },
    {
        id: "physical-design",
        icon: Microchip,
        title: "Physical Design",
        shortDesc: "Synthesis to GDSII",
        color: "#0079C2",
        fullDesc: "End-to-end implementation from RTL to tapeout at advanced nodes",
        capabilities: [
            "Logic synthesis & DFT insertion",
            "Floor planning & power planning",
            "Placement & clock tree synthesis",
            "Routing & timing optimization",
            "Physical verification (LVS/DRC)",
            "Tapeout support & signoff"
        ]
    },
    {
        id: "dft",
        icon: Settings,
        title: "Design for Test",
        shortDesc: "Scan, ATPG, BIST",
        color: "#EE7623",
        fullDesc: "Comprehensive testability solutions for maximum fault coverage",
        capabilities: [
            "Scan chain insertion & optimization",
            "ATPG pattern generation",
            "MBIST & LBIST implementation",
            "JTAG/IEEE 1149.1 integration",
            "Fault simulation & coverage analysis",
            "Test vector compression"
        ]
    },
    {
        id: "analog",
        icon: CircuitBoard,
        title: "Analog/Mixed-Signal",
        shortDesc: "Custom IC Design",
        color: "#9333EA",
        fullDesc: "Precision analog and mixed-signal circuit design",
        capabilities: [
            "Custom analog circuit design",
            "ADC/DAC design & verification",
            "PLL, LDO, and power management",
            "SerDes and high-speed I/O",
            "Custom layout & extraction",
            "AMS verification methodology"
        ]
    },
    {
        id: "post-silicon",
        icon: Activity,
        title: "Post-Silicon Validation",
        shortDesc: "Debug & Characterization",
        color: "#EC4899",
        fullDesc: "Ensuring silicon works as designed in real-world conditions",
        capabilities: [
            "Silicon bring-up & debug",
            "Electrical validation & SI",
            "Performance characterization",
            "System-level testing (SLT)",
            "Failure analysis support",
            "Production test development"
        ]
    },
    {
        id: "embedded",
        icon: Code,
        title: "Embedded & Software",
        shortDesc: "Firmware to Application",
        color: "#10B981",
        fullDesc: "Complete software stack development for semiconductor products",
        capabilities: [
            "Firmware & bootloader development",
            "Linux/RTOS device drivers",
            "BSP development & porting",
            "AI/ML model optimization",
            "Automotive software (AUTOSAR)",
            "IoT & edge computing solutions"
        ]
    }
];

const Capabilities: React.FC = () => {
    const [activeService, setActiveService] = useState(services[0]);

    return (
        <section id="capabilities" className="py-24 bg-gradient-to-b from-[#0a1628] to-[#153860] relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `radial-gradient(circle at 50% 50%, rgba(85, 195, 189, 0.3) 0%, transparent 70%)`
                    }}
                />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#55C3BD]/10 border border-[#55C3BD]/30 text-[#55C3BD] text-xs font-semibold uppercase tracking-wider mb-6">
                        <Layers size={14} /> Turnkey Services
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                        VLSI <span className="text-[#55C3BD]">Engineering Services</span>
                    </h2>
                    <p className="text-lg text-white/60 max-w-3xl mx-auto">
                        Comprehensive semiconductor engineering solutions across the entire product lifecycle
                    </p>
                </div>

                {/* Services Grid */}
                <div className="grid lg:grid-cols-12 gap-8">
                    {/* Service Cards */}
                    <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                        {services.map((service) => {
                            const Icon = service.icon;
                            const isActive = activeService.id === service.id;
                            return (
                                <button
                                    key={service.id}
                                    onClick={() => setActiveService(service)}
                                    className={`text-left p-6 rounded-xl border transition-all duration-300 group ${isActive
                                        ? 'bg-white/10 border-[#55C3BD] shadow-lg shadow-[#55C3BD]/10'
                                        : 'bg-white/5 border-white/10 hover:border-white/30'
                                        }`}
                                >
                                    <div
                                        className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}
                                        style={{ background: `linear-gradient(135deg, ${service.color}40, ${service.color}20)` }}
                                    >
                                        <Icon size={24} style={{ color: service.color }} />
                                    </div>
                                    <h3 className={`text-base font-semibold mb-1 transition-colors ${isActive ? 'text-[#55C3BD]' : 'text-white'}`}>
                                        {service.title}
                                    </h3>
                                    <p className="text-xs text-white/50">{service.shortDesc}</p>
                                </button>
                            );
                        })}
                    </div>

                    {/* Service Details Panel */}
                    <div className="lg:col-span-7">
                        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-10 h-full">
                            <div className="flex items-start gap-6 mb-8">
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
                                    style={{ background: `linear-gradient(135deg, ${activeService.color}, ${activeService.color}80)` }}
                                >
                                    <activeService.icon size={32} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{activeService.title}</h3>
                                    <p className="text-white/60">{activeService.fullDesc}</p>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-3">
                                {activeService.capabilities.map((cap, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                                    >
                                        <div
                                            className="w-2 h-2 rounded-full shrink-0"
                                            style={{ backgroundColor: activeService.color }}
                                        />
                                        <span className="text-sm text-white/80">{cap}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-white/10">
                                <a
                                    href="#contact"
                                    className="inline-flex items-center gap-2 text-[#55C3BD] font-semibold hover:text-white transition-colors"
                                >
                                    Discuss Your {activeService.title} Needs
                                    <ArrowRight size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Capabilities;
